<template>
    <div>
        <h1>Import Users</h1>

        <!-- Show options for csv actions -->
        <div    class="flex flex-row items-center justify-around mb-4 w-full">
            <!-- Button to Upload CSV -->
            <div    class="w-1/3">
                <input  class=""
                        type="file"
                        @change="handleSelectFile">
                <button-blue-primary    class="w-full"
                                        @click="handleUploadCSV"
                >
                    <div    v-if="isUploadingCSV"
                            class="flex items-center">
                        <span   class="lnr icon-loading"></span>
                        <span   class="ml-2">Uploading CSV</span>
                    </div>
                    <div    v-else>
                        Upload CSV
                    </div>
                </button-blue-primary>
            </div>

            <!-- Button to Download Template CSV -->
            <div class="w-1/3">
                <button-blue-secondary  class="w-1/4"
                                        @click="handleDownloadTemplateCSV"
                >Download Template CSV</button-blue-secondary>
            </div>
        </div>

        <!-- Show the uploaded CSV data -->
        <div    v-if="usersData.length > 0">
            <import-users-validate-csv  :roles="roles"
                                        :companies="companies"
                                        :rows-with-errors="rowsWithErrors"
                                        :users-data="usersData"
            ></import-users-validate-csv>
        </div>

        <!-- If the last validation of the user data did not pass, offer revalidation button.  Otherise, save button -->
        <div    v-if="usersData.length > 0 && ! isUploadingCSV"
                class="mt-2">
            <sonicu-button      v-if="isValidatingData"
                                @click="handleRevalidateUsers()">
                <div    v-if="isUploadingCSV"
                        class="flex items-center">
                    <span   class="lnr icon-loading"></span>
                    <span   class="ml-2">Re-validating</span>
                </div>
                <div    v-else>
                    Re-validate Users
                </div>
            </sonicu-button>

            <sonicu-button      v-else
                                @click="handleSaveUsers">
                <div    v-if="isSavingUsers"
                        class="flex items-center">
                    <span   class="lnr icon-loading"></span>
                    <span   class=" ml-2">Saving User</span>
                </div>
                <div   v-else>Save User</div>
            </sonicu-button>
        </div>

        <!-- Users Saved Banner -->
        <success-message    v-if="isUsersSaved">
            Users Saved
        </success-message>

        <error-message   v-if="errorMessage.length > 0">
            {{ errorMessage }}
        </error-message>
    </div>
</template>

<script>
import axios from "axios"
import _ from "lodash"
import {SonicuCoreMixin} from "@/mixins/SonicuCoreMixin";
import {iconNamesMixin} from '@/mixins/IconNamesMixin';

import SonicuButton from "@/components/elements/buttons/SonicuButton.vue";
import ButtonBluePrimary from "@/components/elements/buttons/ButtonBluePrimary.vue";
import ButtonBlueSecondary from "@/components/elements/buttons/ButtonBlueSecondary.vue";
import ImportUsersValidateCsv from "@/components/Users/ImportUsersValidateCsv.vue";
import SuccessMessage from "@/components/SuccessMessage.vue";
import BannerError from "@/components/Banners/BannerError.vue";
import ErrorMessage from "@/components/ErrorMessage.vue";

export default {
    name: "ImportUsersIndex",
    mixins: [SonicuCoreMixin, iconNamesMixin],
    components: {
        ErrorMessage,
        BannerError,
        SuccessMessage, ImportUsersValidateCsv, ButtonBlueSecondary, ButtonBluePrimary, SonicuButton},
    emits: [],
    props: {
        roles: {
            type: Object,
            required: true,
        },
        companies: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            // State Tracking Boolean Flags
            isUploadingCSV: false,
            isDownloadingTemplateCSV: false,
            isUsersSaved: false,

            // Error Message
            errorMessage: '',

            // File Info
            fileInfo: null,

            // Users Data
            isValidatingData: false,
            isSavingUsers: false,
            usersData: [],
            rowsWithErrors: [],
        }
    },
    created() {

    },
    mounted() {

    },
    methods: {
        handleSelectFile(event) {
            // Get the selected files and ensure there are only 1
            const files = event.target.files
            if (files.length !== 1) {
                this.errorMessage = 'Please select a single file.'
                return
            }

            // Get the file and read it in
            let file = files[0]
            const reader = new FileReader()

            reader.onload = (e) => {
                const fileContents = e.target.result
                this.fileInfo = fileContents
                console.log('File Contents:', fileContents)
            }
            reader.readAsText(file)
        },
        handleUploadCSV() {
            this.uploadUsersData(true, 'upload')
        },
        handleRevalidateUsers() {
            this.uploadUsersData(true, 'revalidate')
        },
        uploadUsersData(isTesting = true, action = 'upload') {
            this.isUploadingCSV = true
            this.isValidatingData = false
            this.errorMessage = ''

            // To upload the file as a file, use FormData
            const formData = new FormData()
            formData.append('action', action) // 'upload' or 'revalidate'
            formData.append('should_test_values_only', JSON.stringify(isTesting))
            if (action === 'revalidate') {
                formData.append('user_data', JSON.stringify(this.usersData))
            }
            else {
                // If we are not revalidating existing data, assume we are uploading.
                formData.append('users_csv', this.fileInfo)
            }

            axios.post(this.company.buildCompanyRoute(`import-users`), formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                },
            })
                .then(response => {
                    if (! isTesting && response.data.saved) {
                        this.isUsersSaved = true
                        this.usersData = []
                        setTimeout(() => {
                            this.isUsersSaved = false
                        }, 5000)
                        return
                    }

                    this.usersData = response.data.users
                    this.rowsWithErrors = []

                    // Find the rows with errors
                    this.isValidatingData = false
                    _.each(this.usersData, (user, index) => {
                        if (_.size(user.errors) > 0) {
                            this.rowsWithErrors.push(index)
                            this.isValidatingData = true
                        }
                    })
                })
                .catch(error => {
                    this.errorMessage = 'There was an error uploading the CSV file.'
                    this.usersData = []
                    setTimeout(() => {
                        this.errorMessage = ''
                    }, 5000)
                })
                .finally(() => {
                    console.log('Finally')
                    this.isUploadingCSV = false
                    this.isSavingUsers = false
                })
        },
        handleDownloadTemplateCSV() {
            this.isDownloadingTemplateCSV = true
            this.errorMessage = ''

            axios.get(this.company.buildCompanyRoute('import-users/template-csv'))
                .then(response => {
                    this.downloadCsv('template.csv', response.data)
                })
                .catch(error => {
                    this.errorMessage = 'There was an error downloading the template CSV file.'
                })
                .finally(() => {
                    this.isDownloadingTemplateCSV = false
                })
        },
        downloadCsv(filename, data) {
            let csvData = new Blob([data], {type: 'text/csv;charset=utf-8;'});

            // Create an object & link to download the csv file
            let csvUrl = URL.createObjectURL(csvData);
            let hiddenElement = document.createElement('a');
            hiddenElement.href = csvUrl;
            hiddenElement.target = '_blank';

            // Add the csv to the DOM and trigger the download
            hiddenElement.download = filename;
            document.body.appendChild(hiddenElement);
            hiddenElement.click();

            // Remove it from the DOM and cleanup
            document.body.removeChild(hiddenElement);
            URL.revokeObjectURL(csvUrl);
        },
        handleSaveUsers(event) {
            this.isSavingUsers = true
            this.uploadUsersData(false, 'revalidate')
        },
    },
    computed: {},
    watch: {},
}
</script>

<style scoped>

</style>